/**
 * Sticky one-way downgrade policy — per-peer, stateful.
 *
 * The pure monotone ratchet lives in ./stickyDowngradeContracts; this class holds the
 * per-peer session floor and exposes the queries the host's recovery manager needs: the
 * current floor, whether any climb is blocked, and a mapping from the latency-first
 * correction levers to the floor each implies. The floor never decreases on a hunch
 * (PRODUCT DECISION D1, as amended): once degraded for cause, the peer holds the floor until
 * a caller holding MEASURED evidence that the cause is gone releases it one rung at a time
 * (`release`, driven by the host's reclaim manager), or the session ends (clearPeer).
 *
 * An fps ceiling ratchets down the same way; `raiseFpsCeiling` is the evidenced counterpart
 * that restores one rung when a floor is released.
 */

import {
  applySessionDowngrade,
  applySessionRelease,
  floorAtLeast,
  initialSessionDowngradeState,
  type SessionDowngradeFloor,
  type SessionDowngradeState,
} from './stickyDowngradeContracts';
import type { LatencyCorrectionLever } from './latencyFirstCorrection';

/**
 * Lever → floor mapping (frozen). `idr` maps to no floor change: the IDR is a one-shot clear,
 * not a degradation. `resolution` is the only lever that maps to `tier360`, so a latency
 * problem never silently downscales via the bitrate/fps levers.
 */
const LEVER_FLOOR: Record<LatencyCorrectionLever, SessionDowngradeFloor | null> = {
  bitrate: 'soft',
  fps: 'hard',
  resolution: 'tier360',
  idr: null,
};

export type StickyFloorEncoding = {
  maxBitrateBps: number;
  maxFramerate: number;
};

export type StickyFloorEncodeContext = {
  profileMaxFps: number;
  fpsCeiling: number;
  /** Applied cut bitrate for soft/hard/tier360 floors; ignored for `none`. */
  cutBitrateBps?: number;
};

/**
 * Pure helper shared by CRM reclaim priming and PCM apply paths.
 * Bitrate is ≤ profile (and ≤ cut when provided for degraded floors).
 * Framerate is ≤ both profile max and the sticky fps ceiling.
 */
export function stickyFloorEncoding(
  floor: SessionDowngradeFloor,
  profileMaxBps: number,
  encodeContext: StickyFloorEncodeContext,
): StickyFloorEncoding {
  const profileBps =
    Number.isFinite(profileMaxBps) && profileMaxBps > 0 ? profileMaxBps : 1;
  const profileFps =
    Number.isFinite(encodeContext.profileMaxFps) && encodeContext.profileMaxFps > 0
      ? encodeContext.profileMaxFps
      : 1;
  const fpsCeiling =
    Number.isFinite(encodeContext.fpsCeiling) && encodeContext.fpsCeiling > 0
      ? encodeContext.fpsCeiling
      : profileFps;
  const maxFramerate = Math.min(profileFps, fpsCeiling);

  if (floor === 'none') {
    return { maxBitrateBps: profileBps, maxFramerate };
  }

  const cut = encodeContext.cutBitrateBps;
  const maxBitrateBps =
    cut !== undefined && Number.isFinite(cut) && cut > 0
      ? Math.min(profileBps, cut)
      : profileBps;

  return { maxBitrateBps, maxFramerate };
}

export class StickyDowngradePolicy {
  private readonly nowMs: () => number;
  private readonly peers = new Map<string, SessionDowngradeState>();
  /** Per-peer fps ceiling; absent means no sticky fps limit beyond the profile max. */
  private readonly fpsCeilings = new Map<string, number>();

  constructor(opts?: { nowMs?: () => number }) {
    this.nowMs = opts?.nowMs ?? (() => Date.now());
  }

  /** Raise a peer's floor to at least `floor`. Idempotent; never lowers. */
  raise(peerId: string, floor: SessionDowngradeFloor, reason: string): SessionDowngradeState {
    const current = this.stateFor(peerId);
    const next = applySessionDowngrade(current, floor, this.nowMs(), reason);
    if (next !== current) this.peers.set(peerId, next);
    return next;
  }

  /** Map a latency-first lever to the floor it implies, then raise. No-op for `idr`. */
  raiseForLever(peerId: string, lever: LatencyCorrectionLever, reason: string): SessionDowngradeState {
    const floor = LEVER_FLOOR[lever];
    if (floor === null) return this.stateFor(peerId);
    return this.raise(peerId, floor, reason);
  }

  /**
   * Evidenced release: lower a peer's floor to `to`. Never raises — a request at or above the
   * current floor returns the current state unchanged (same identity). The raise metadata is
   * preserved beside `releasedAtMs`/`releaseReason` so the record shows both directions.
   */
  release(peerId: string, to: SessionDowngradeFloor, reason: string): SessionDowngradeState {
    const current = this.stateFor(peerId);
    const next = applySessionRelease(current, to, this.nowMs(), reason);
    if (next !== current) this.peers.set(peerId, next);
    return next;
  }

  /** The full per-peer record (floor plus raise/release metadata); the initial state when never degraded. */
  stateFor(peerId: string): SessionDowngradeState {
    return this.peers.get(peerId) ?? initialSessionDowngradeState();
  }

  /** Current floor for a peer ('none' when never degraded). */
  floorFor(peerId: string): SessionDowngradeFloor {
    return this.peers.get(peerId)?.floor ?? 'none';
  }

  /** True once any floor above 'none' is set — a climb is blocked for the rest of the session. */
  blocksClimb(peerId: string): boolean {
    return floorAtLeast(this.floorFor(peerId), 'soft');
  }

  /**
   * Ratchet the peer's fps ceiling down. Never raises. Non-finite / non-positive values are ignored.
   */
  lowerFpsCeiling(peerId: string, fps: number): void {
    if (!Number.isFinite(fps) || fps <= 0) return;
    const current = this.fpsCeilings.get(peerId);
    if (current === undefined || fps < current) {
      this.fpsCeilings.set(peerId, fps);
    }
  }

  /**
   * Evidenced counterpart of `lowerFpsCeiling`: raise the recorded ceiling to `fps`. A no-op
   * when no ceiling is recorded (nothing to restore), when `fps` is not above the current
   * ceiling, or when `fps` is non-finite / non-positive. `fpsCeilingFor` still clamps the
   * result to the profile max, so a raise can never force fps above the profile.
   */
  raiseFpsCeiling(peerId: string, fps: number): void {
    if (!Number.isFinite(fps) || fps <= 0) return;
    const current = this.fpsCeilings.get(peerId);
    if (current === undefined || fps <= current) return;
    this.fpsCeilings.set(peerId, fps);
  }

  /**
   * Effective fps ceiling for priming/climb: never above the sticky ceiling or the profile max.
   * With no sticky ceiling recorded, returns `profileMaxFps`.
   */
  fpsCeilingFor(peerId: string, profileMaxFps: number): number {
    const profile =
      Number.isFinite(profileMaxFps) && profileMaxFps > 0 ? profileMaxFps : 1;
    const ceiling = this.fpsCeilings.get(peerId);
    if (ceiling === undefined) return profile;
    return Math.min(profile, ceiling);
  }

  clearPeer(peerId: string): void {
    this.peers.delete(peerId);
    this.fpsCeilings.delete(peerId);
  }

  clearAll(): void {
    this.peers.clear();
    this.fpsCeilings.clear();
  }
}
